import { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormContext } from 'react-hook-form'
import { XCloseIcon, MarkerPin01Icon, Loading01Icon } from '@untitledui-icons/react/line'
import { cn } from '@/lib/utils'
import { usePlaceAutocomplete, usePlaceDetails, type PlacePrediction } from '@/hooks/api'

interface GooglePlacesAutocompleteProps {
  name: string
  label?: string
  description?: string
  className?: string
}

/**
 * Google Places Autocomplete component for selecting a business location.
 * Shows a search input that displays autocomplete predictions,
 * and shows selected place details with a photo and address.
 */
export function GooglePlacesAutocomplete({
  name,
  label,
  description,
  className,
}: GooglePlacesAutocompleteProps) {
  const { t } = useTranslation()
  const { setValue, watch } = useFormContext()
  const placeId = watch(name)
  
  const [inputValue, setInputValue] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Fetch autocomplete predictions
  const { data: predictions = [], isLoading: isLoadingPredictions } = usePlaceAutocomplete(
    inputValue,
    isOpen && inputValue.length >= 3
  )

  // Fetch place details for selected place
  const { data: placeDetails, isLoading: isLoadingDetails } = usePlaceDetails(placeId)

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Handle selecting a prediction
  const handleSelect = useCallback(
    (prediction: PlacePrediction) => {
      setValue(name, prediction.place_id, { shouldDirty: true })
      setInputValue('')
      setIsOpen(false)
      setSelectedIndex(-1)
    },
    [name, setValue]
  )

  // Handle clearing the selection
  const handleClear = useCallback(() => {
    setValue(name, null, { shouldDirty: true })
    setInputValue('')
  }, [name, setValue])

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!predictions.length) return

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setSelectedIndex((prev) => (prev < predictions.length - 1 ? prev + 1 : prev))
          break
        case 'ArrowUp':
          e.preventDefault()
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev))
          break
        case 'Enter':
          e.preventDefault()
          if (selectedIndex >= 0 && predictions[selectedIndex]) {
            handleSelect(predictions[selectedIndex])
          }
          break
        case 'Escape':
          setIsOpen(false)
          setSelectedIndex(-1)
          break
      }
    },
    [predictions, selectedIndex, handleSelect]
  )

  // Reset selected index when predictions change
  useEffect(() => {
    setSelectedIndex(-1)
  }, [predictions])

  // If we have a selected place, show the place card
  const displayLabel = label ?? t('resources.assistants.google_places.label')
  const displayDescription = description ?? t('resources.assistants.about.location_description')

  if (placeId && placeDetails) {
    return (
      <div className={cn('flex flex-col gap-1.5', className)}>
        <label className="text-sm font-medium text-gray-700 dark:text-gray-200">
          {displayLabel}
        </label>
        <p className="text-sm text-gray-500 dark:text-gray-400">{displayDescription}</p>
        <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900">
          {/* Place photo */}
          {placeDetails.photoUrl ? (
            <img
              src={placeDetails.photoUrl}
              alt={placeDetails.name}
              className="h-12 w-12 flex-shrink-0 rounded-lg object-cover"
            />
          ) : (
            <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-brand-100 dark:bg-brand-900">
              <MarkerPin01Icon className="h-6 w-6 text-brand-600 dark:text-brand-400" />
            </div>
          )}

          {/* Place info */}
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {placeDetails.name}
            </span>
            {placeDetails.address && (
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {placeDetails.address}
              </span>
            )}
            {placeDetails.city && (
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {placeDetails.city}
              </span>
            )}
          </div>

          {/* Remove button */}
          <button
            type="button"
            onClick={handleClear}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            {t('resources.generic.remove')}
            <XCloseIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    )
  }

  // Show loading state while fetching details
  if (placeId && isLoadingDetails) {
    return (
      <div className={cn('flex flex-col gap-1.5', className)}>
        <label className="text-sm font-medium text-gray-700 dark:text-gray-200">
          {displayLabel}
        </label>
        <p className="text-sm text-gray-500 dark:text-gray-400">{displayDescription}</p>
        <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900">
          <Loading01Icon className="h-5 w-5 animate-spin text-gray-400" />
          <span className="text-sm text-gray-500">{t('resources.assistants.google_places.loading_details')}</span>
        </div>
      </div>
    )
  }

  // Show search input
  return (
    <div ref={containerRef} className={cn('relative flex flex-col gap-1.5', className)}>
      <label className="text-sm font-medium text-gray-700 dark:text-gray-200">
        {displayLabel}
      </label>
      <p className="text-sm text-gray-500 dark:text-gray-400">{displayDescription}</p>

      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value)
            setIsOpen(true)
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={t('resources.assistants.google_places.placeholder')}
          className={cn(
            'w-full rounded-lg border border-gray-300 bg-white px-3.5 py-2.5 pr-10 text-base',
            'placeholder:text-gray-400',
            'focus:border-brand-500 focus:ring-1 focus:ring-brand-500 focus:outline-none',
            'dark:bg-gray-900 dark:border-gray-700 dark:text-gray-100',
            'dark:focus:border-brand-400 dark:focus:ring-brand-400'
          )}
        />
        {isLoadingPredictions && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <Loading01Icon className="h-5 w-5 animate-spin text-gray-400" />
          </div>
        )}
      </div>

      {/* Predictions dropdown */}
      {isOpen && predictions.length > 0 && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-60 overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-900">
          {predictions.map((prediction, index) => (
            <button
              key={prediction.place_id}
              type="button"
              onClick={() => handleSelect(prediction)}
              className={cn(
                'w-full px-3 py-2 text-left text-sm',
                'hover:bg-gray-100 dark:hover:bg-gray-800',
                selectedIndex === index && 'bg-gray-100 dark:bg-gray-800'
              )}
            >
              {prediction.structured_formatting ? (
                <>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {prediction.structured_formatting.main_text}
                  </span>
                  <br />
                  <span className="text-gray-500 dark:text-gray-400">
                    {prediction.structured_formatting.secondary_text}
                  </span>
                </>
              ) : (
                <span className="text-gray-900 dark:text-gray-100">
                  {prediction.description}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default GooglePlacesAutocomplete
